import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'motion/react';
import { cn } from '../lib/utils';
import { useExperiment, useStatsigClient } from '@statsig/react-bindings';
import { useIsMobile } from '../hooks/useIsMobile';
import { SkeletonBranching, SkeletonVisualFlow, SkeletonNotes, SkeletonCollaboration, SkeletonKnowledgeManagement } from '../components/BentoGrid';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';

interface SignupForm {
  email: string;
  password: string;
}

// Lazy load heavy components
const ExpandableCards = lazy(() => import('../components/ExpandableCards').then(module => ({ default: module.ExpandableCards })));
const AnimatedChatScreenshot = lazy(() => import('../components/AnimatedChatScreenshot'));
const BentoGrid = lazy(() => import('../components/BentoGrid').then(module => ({ default: module.BentoGrid })));

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, register: registerUser, loginWithGoogle } = useAuth();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredModelCard, setHoveredModelCard] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const videoRef = React.useRef<HTMLDivElement>(null);
  const [videoSrc, setVideoSrc] = React.useState<string>('');

  // Statsig A/B test for hero title
  const experiment = useExperiment('hero_title_test');
  const statsigClient = useStatsigClient();

  // Get the hero title variant from the experiment
  // Default to control if Statsig is not configured
  const heroTitleVariant = experiment.get('variant', 'control');

  // Form handling
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupForm>();

  const password = watch('password');

  const validatePassword = (value: string) => {
    if (value.length < 6) return 'Password must be at least 6 characters';
    return true;
  };

  // Handle email/password signup
  const onSubmit = async (data: SignupForm) => {
    try {
      setIsSubmitting(true);

      // Log signup attempt
      if (statsigClient) {
        statsigClient.logEvent('signup_form_submitted', undefined, {
          method: 'email_password',
          location: 'hero',
          variant: heroTitleVariant,
        });
      }

      await registerUser(data.email, data.password);
      navigate('/dashboard');
    } catch (error) {
      // Error handled in AuthContext with toast
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Google OAuth signup
  const handleGoogleSignUp = async () => {
    // Log Google signup click
    if (statsigClient) {
      statsigClient.logEvent('signup_oauth_clicked', undefined, {
        provider: 'google',
        location: 'hero',
        variant: heroTitleVariant,
      });
    }

    try {
      setIsSubmitting(true);
      await loginWithGoogle();
      // OAuth will redirect automatically
    } catch (error) {
      // Error handled in AuthContext with toast
      setIsSubmitting(false);
    }
  };

  // Set page title to just "Ramify"
  useEffect(() => {
    document.title = 'Ramify';
  }, []);

  // Autoplay video when scrolled into view
  useEffect(() => {
    const currentRef = videoRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !videoSrc) {
            // Load the video with autoplay when it comes into view (with sound)
            setVideoSrc('https://www.youtube.com/embed/796c3BNgR4s?autoplay=1');
          }
        });
      },
      {
        threshold: 0.25, // Trigger when 25% of the video is visible
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [videoSrc]);

  const features = [
    {
      title: "Branch Anywhere",
      description: "Split any conversation at any point to explore different ideas without losing context.",
      skeleton: <SkeletonBranching />,
      className: "col-span-1 lg:col-span-6 border-b border-white/10 overflow-visible",
    },
    {
      title: "Visual Flow",
      description: "See your conversation tree grow. Get a bird's eye view of your ideas with Node Summaries and Tree Summaries. Navigate between different paths of thought.",
      skeleton: <SkeletonVisualFlow />,
      className: "col-span-1 lg:col-span-3 border-b lg:border-r border-white/10",
    },
    {
      title: "Crystallise your Insights",
      description: "Record insights worth remembering by take notes alongside your conversations. Keep all your thoughts, insights, and research in one organized place. ",
      skeleton: <SkeletonNotes />,
      className: "col-span-1 lg:col-span-3 border-b border-white/10",
    },
    {
      title: "AI-Native Knowledge Management",
      description: "Organize your thoughts, conversations, and insights in a system designed for the AI era. Equipped with state-of-the-art tagging systems, powerful search capabilities and Zettelkasten-inspired linking. All seamlessly integrated.",
      skeleton: <SkeletonKnowledgeManagement />,
      className: "col-span-1 lg:col-span-4 lg:border-r border-white/10",
    },
    {
      title: "Collaborate Effortlessly",
      description: "Collaborate on AI chats and Notes like you collaborate on Google Docs. Work together in real-time, share insights effortlessly. ",
      skeleton: <SkeletonCollaboration />,
      className: "col-span-1 lg:col-span-2",
    },
  ];

  const useCases = [
    {
      title: "PhD Students & Researchers",
      description: "Navigate complex literature reviews, explore competing theories, and keep track of every research thread without losing your place",
      details: [
        "Branch conversations to explore alternative hypotheses",
        "Maintain context across multiple research papers",
        "Track your thought process through complex theoretical frameworks",
        "Compare different methodological approaches side by side"
      ]
    },
    {
      title: "Writers & Content Creators",
      description: "Develop intricate storylines, explore character backstories, and follow tangents that enrich your narrative—all in one place",
      details: [
        "Explore alternate plot developments without losing your main storyline",
        "Deep dive into character backgrounds and motivations",
        "Experiment with different narrative voices and styles",
        "Keep track of world-building details across multiple threads"
      ]
    },
    {
      title: "Product Teams",
      description: "Brainstorm features, explore edge cases, and document decision trees collaboratively while maintaining context across branches",
      details: [
        "Explore multiple feature implementations in parallel",
        "Document decision-making processes with full context",
        "Test different user scenarios and edge cases",
        "Maintain alignment across distributed team discussions"
      ]
    },
    {
      title: "Developers & Engineers",
      description: "Debug complex systems by exploring different hypotheses, test multiple solutions in parallel, and trace your problem-solving journey",
      details: [
        "Branch to test different debugging approaches simultaneously",
        "Document your problem-solving process for future reference",
        "Explore multiple solution architectures in parallel",
        "Keep context when switching between different technical approaches"
      ]
    }
  ];


  return (
    <div>
    {/* Black background */}
    <div className="fixed inset-0 w-full h-full bg-black z-0"></div>

    {/* Navigation with glassmorphism */}
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/20">
      <div className="flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <img
            src="/ramify_logo_white.svg"
            alt="Ramify"
            className="h-8 sm:h-10 w-auto ml-0 transition-all duration-400 hover:scale-110"
            style={{
              filter: 'drop-shadow(0 0 0 transparent)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.6)) drop-shadow(0 0 40px rgba(255, 140, 50, 0.4))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'drop-shadow(0 0 0 transparent)';
            }}
          />
          <span className="text-white text-lg sm:text-xl font-medium" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
            Ramify
          </span>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          <a href="/docs" className="text-white/80 hover:text-white transition-all duration-300 text-sm font-medium">
            Docs
          </a>
          <a href="/about" className="text-white/80 hover:text-white transition-all duration-300 text-sm font-medium">
            About
          </a>
          <a href="/team" className="text-white/80 hover:text-white transition-all duration-300 text-sm font-medium">
            Team
          </a>
          {!user ? (
            <button
              onClick={() => navigate('/login')}
              className="backdrop-blur-sm bg-white/10 border border-white/20 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-white/20 transition-all duration-300 hover:scale-105"
            >
              Sign In
            </button>
          ) : (
            <button
              onClick={() => navigate('/dashboard')}
              className="backdrop-blur-sm bg-white/10 border border-white/20 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-white/20 transition-all duration-300 hover:scale-105"
            >
              Dashboard
            </button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden backdrop-blur-md bg-black/90 border-t border-white/10">
          <div className="px-4 py-4 space-y-3">
            <a href="/docs" className="block text-white/80 hover:text-white transition-all duration-300 text-base font-medium py-2">
              Docs
            </a>
            <a href="/about" className="block text-white/80 hover:text-white transition-all duration-300 text-base font-medium py-2">
              About
            </a>
            <a href="/team" className="block text-white/80 hover:text-white transition-all duration-300 text-base font-medium py-2">
              Team
            </a>
            {!user ? (
              <button
                onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                className="w-full backdrop-blur-sm bg-white/10 border border-white/20 text-white px-5 py-3 rounded-lg text-base font-medium hover:bg-white/20 transition-all duration-300"
              >
                Sign In
              </button>
            ) : (
              <button
                onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                className="w-full backdrop-blur-sm bg-white/10 border border-white/20 text-white px-5 py-3 rounded-lg text-base font-medium hover:bg-white/20 transition-all duration-300"
              >
                Dashboard
              </button>
            )}
          </div>
        </div>
      )}
    </nav>

    {/* Hero Section Container */}
    <div className="relative overflow-hidden min-h-screen md:min-h-[150vh]">
      {/* Hero Section */}
      <div className="bg-transparent relative z-20 pt-24 md:pt-32 pb-20 md:pb-40 lg:pb-60 px-4 sm:px-6 lg:px-8">

        {/* Two-column layout: Text left, Signup right */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-8 lg:gap-20 items-start">

          {/* Left Column - Hero Text */}
          <div className="text-left">
            <h1 className="bg-gradient-to-br from-white via-white to-slate-400 py-4 bg-clip-text text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-transparent">
              Your thoughts don't move in straight lines. Why should your AI conversations?
            </h1>

            <p
              className="mt-6 md:mt-8 text-base sm:text-lg md:text-xl text-white/70 leading-relaxed"
              style={{
                fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: 400
              }}
            >
              Ramify is a <span className="font-semibold text-white">visual AI workspace</span> that connects your <span className="font-semibold text-white">branching AI chats</span>, <span className="font-semibold text-white">research</span>, and <span className="font-semibold text-white">notes</span> in one place. Stop losing context, start making breakthroughs.
            </p>

            {/* Trust signals */}
            <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-4 md:gap-6">
              <div className="flex items-center gap-2 text-sm md:text-base text-white/60">
                <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Free forever for core features</span>
              </div>
              <div className="flex items-center gap-2 text-sm md:text-base text-white/60">
                <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2 text-sm md:text-base text-white/60">
                <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Start in 30 seconds</span>
              </div>
            </div>

            {/* University Logos - Mobile Only (appears before signup form) */}
            <div className="mt-8 lg:hidden">
              <p className="text-sm text-white/40 font-light tracking-wide text-center mb-4">
                Trusted by researchers at
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 px-4">
                {/* Australian National University */}
                <div className="opacity-100 transition-all duration-300">
                  <img
                    src="/anu-logo.svg"
                    alt="Australian National University"
                    className="h-10 w-auto object-contain"
                  />
                </div>

                {/* Queensland University of Technology */}
                <div className="opacity-100 transition-all duration-300">
                  <img
                    src="/qut-logo.svg"
                    alt="Queensland University of Technology"
                    className="h-10 w-auto object-contain"
                  />
                </div>

                {/* University of Queensland */}
                <div className="opacity-100 transition-all duration-300">
                  <img
                    src="/uq-logo.svg"
                    alt="University of Queensland"
                    className="h-10 w-auto object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Screenshot - Mobile Only (appears before signup form) */}
            <div className="mt-8 lg:hidden">
              <Suspense fallback={<div className="h-[300px] flex items-center justify-center"><div className="animate-pulse text-white/40">Loading...</div></div>}>
                <div className="flex justify-center overflow-visible" style={{ perspective: '1500px' }}>
                  <div className="w-full" style={{
                    transform: 'rotateX(30deg) rotateY(-1deg) rotateZ(-10deg)',
                    transformOrigin: 'center center',
                    position: 'relative',
                    marginTop: '10px'
                  }}>
                    <AnimatedChatScreenshot compact={true} wideContainer={true} />
                  </div>
                </div>
              </Suspense>
            </div>
          </div>

          {/* Right Column - Signup Form */}
          <div className="relative z-30 w-full max-w-md mx-auto lg:mx-0 lg:max-w-md">
          <div>
            {/* Google Sign-Up Button - PRIMARY */}
            <button
              type="button"
              onClick={handleGoogleSignUp}
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              style={{
                backgroundColor: '#ffffff',
                color: '#1f2937',
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Recommendation badge */}
            <div className="text-center mt-2 mb-5">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full" style={{
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                color: '#22c55e',
                border: '1px solid rgba(34, 197, 94, 0.2)'
              }}>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                Fastest - Sign up in 5 seconds
              </span>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.15)' }}></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 py-1 rounded-full" style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>Or sign up with email</span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  type="email"
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-lg text-sm sm:text-base transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    border: errors.email ? '1px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    outline: 'none',
                  }}
                  placeholder="Enter your email"
                  onFocus={(e) => {
                    if (!errors.email) {
                      e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.email) {
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                    }
                  }}
                />
                {errors.email && (
                  <p className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="relative">
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      validate: validatePassword,
                    })}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="w-full px-4 py-3 pr-11 rounded-lg text-sm sm:text-base transition-all"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      border: errors.password ? '1px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      outline: 'none',
                    }}
                    placeholder="Create a password"
                    onFocus={(e) => {
                      if (!errors.password) {
                        e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      if (!errors.password) {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-white/40 hover:text-white/60 transition-colors" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-white/40 hover:text-white/60 transition-colors" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-xs text-red-400">{errors.password.message}</p>
                )}
                {password && !errors.password && (
                  <p className="mt-1.5 text-xs" style={{ color: password.length >= 6 ? '#22c55e' : 'rgba(255, 255, 255, 0.5)' }}>
                    {password.length >= 6 ? '✓ ' : ''}At least 6 characters
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 text-base font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner size="sm" color="white" />
                    <span className="ml-2">Getting started...</span>
                  </div>
                ) : (
                  'Get Started'
                )}
              </button>
            </form>
          </div>
          </div>
          {/* End Right Column */}

        </div>
        {/* End Two-column grid */}

        {/* University Logos - Social Proof (Desktop Only) */}
        <div className="mt-8 md:mt-12 mb-8 md:mb-10 hidden lg:block">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8 lg:gap-12 px-4">
            <p className="text-sm md:text-base text-white/40 font-light tracking-wide">
              Trusted by researchers at
            </p>
            {/* Australian National University */}
            <div className="opacity-100 transition-all duration-300">
              <img
                src="/anu-logo.svg"
                alt="Australian National University"
                className="h-12 md:h-14 w-auto object-contain"
              />
            </div>

            {/* Queensland University of Technology */}
            <div className="opacity-100 transition-all duration-300">
              <img
                src="/qut-logo.svg"
                alt="Queensland University of Technology"
                className="h-12 md:h-14 w-auto object-contain"
              />
            </div>

            {/* University of Queensland */}
            <div className="opacity-100 transition-all duration-300">
              <img
                src="/uq-logo.svg"
                alt="University of Queensland"
                className="h-12 md:h-14 w-auto object-contain"
              />
            </div>
          </div>
        </div>

        {/* Screenshot - Desktop Only */}
        <div className="hidden lg:block">
        <Suspense fallback={<div className="mt-12 h-[500px] flex items-center justify-center"><div className="animate-pulse text-white/40">Loading...</div></div>}>
          {isMobile ? (
            /* Mobile: Screenshot with 3D rotation */
            <div className="mt-12 flex justify-center overflow-visible" style={{ perspective: '1500px' }}>
              <div className="w-full" style={{
                transform: 'rotateX(30deg) rotateY(-1deg) rotateZ(-10deg)',
                transformOrigin: 'center center',
                position: 'relative',
                marginTop: '10px'
              }}>
                <AnimatedChatScreenshot compact={true} wideContainer={true} />
              </div>
            </div>
          ) : (
            /* Desktop: Original 3D transformed screenshot */
            <div className="mt-4 overflow-visible relative" style={{ perspective: '2000px' }}>
              <style>{`
                @media (min-width: 768px) {
                  .screenshot-transform {
                    transform: rotateX(40deg) rotateY(-2deg) rotateZ(-10deg) scale(1.2) !important;
                  }
                }
              `}</style>
              <div className="screenshot-transform ml-[15%] mr-[-30%]" style={{
                position: 'relative'
              }}>
                <AnimatedChatScreenshot compact={false} wideContainer={false} />
                {/* Gradient overlays for smooth blending */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '200px',
                  background: 'linear-gradient(to top, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%)',
                  pointerEvents: 'none',
                  zIndex: 10
                }} />
                <div style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  width: '400px',
                  background: 'linear-gradient(to left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.8) 30%, rgba(0, 0, 0, 0) 100%)',
                  pointerEvents: 'none',
                  zIndex: 10
                }} />
                <div style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: '400px',
                  background: 'linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.8) 30%, rgba(0, 0, 0, 0) 100%)',
                  pointerEvents: 'none',
                  zIndex: 10
                }} />
              </div>
            </div>
          )}
        </Suspense>
        </div>
        {/* End Desktop Screenshot */}

        {/* Video Section */}
        <div ref={videoRef} className="mt-16 md:mt-24 mb-16 md:mb-20 relative z-50">
          <div className="max-w-5xl mx-auto px-4">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-xl"
                src={videoSrc || 'https://www.youtube.com/embed/796c3BNgR4s'}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  border: 'none',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                }}
              />
            </div>
          </div>
        </div>

        {/* What is Ramify? Section */}
        <div className="mt-20 md:mt-32 mb-16 md:mb-20 relative z-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-8 md:mb-12 text-center">
              What is Ramify?
            </h2>

            {/* The Problem */}
            <div className="mb-10 md:mb-12">
              <h3 className="text-xl md:text-2xl font-medium text-white/90 mb-4 text-center">
                The problem Ramify solves:
              </h3>
              <p className="text-base md:text-lg text-white/70 leading-relaxed mb-4 text-justify">
                Suppose you're using ChatGPT to research quantum computing. Claude suggests an interesting angle about error correction. You explore it—but now you've lost your original thread about quantum entanglement.
              </p>
              <p className="text-base md:text-lg text-white/70 leading-relaxed mb-4 text-justify">
                You start a new chat. Then another. And another.
              </p>
              <p className="text-base md:text-lg text-white/70 leading-relaxed text-justify">
                Before long, you have 50 disconnected conversations and no way to remember how they connect. Your best insights are buried. Context is lost forever.
              </p>
            </div>

            {/* How Ramify Fixes It */}
            <div className="mb-10 md:mb-12">
              <h3 className="text-xl md:text-2xl font-medium text-white/90 mb-4 text-center">
                How Ramify solves it:
              </h3>
              <p className="text-base md:text-lg text-white/70 leading-relaxed mb-4 text-justify">
                Ramify lets you <span className="font-semibold text-emerald-400">branch</span> any conversation at any point—like branching code, but for ideas.
              </p>
              <p className="text-base md:text-lg text-white/70 leading-relaxed mb-4 text-justify">
                Select any text in an AI response. Click "Ramify." Now you're exploring a tangent while your original conversation stays intact. You can see both paths. Compare them. Jump between them.
              </p>
              <p className="text-base md:text-lg text-white/70 leading-relaxed text-justify">
                Your conversations become <span className="font-semibold text-emerald-400">visual trees</span> you can navigate, not linear chat logs you scroll through.
              </p>
            </div>

            {/* But It Goes Further */}
            <div className="mb-10 md:mb-12">
              <h3 className="text-xl md:text-2xl font-medium text-white/90 mb-6 text-center">
                But it goes further:
              </h3>
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                <div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-5 md:p-6">
                  <h4 className="text-white font-semibold mb-2 text-base md:text-lg">Notes that link to chats</h4>
                  <p className="text-white/70 text-sm md:text-base text-justify">Capture insights alongside conversations, Obsidian-style</p>
                </div>
                <div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-5 md:p-6">
                  <h4 className="text-white font-semibold mb-2 text-base md:text-lg">Automatic research discovery</h4>
                  <p className="text-white/70 text-sm md:text-base text-justify">See 25M academic papers semantically related to your work</p>
                </div>
                <div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-5 md:p-6">
                  <h4 className="text-white font-semibold mb-2 text-base md:text-lg">Real-time collaboration</h4>
                  <p className="text-white/70 text-sm md:text-base text-justify">Share entire conversation trees with your team</p>
                </div>
                <div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-5 md:p-6">
                  <h4 className="text-white font-semibold mb-2 text-base md:text-lg">Never lose context</h4>
                  <p className="text-white/70 text-sm md:text-base text-justify">AI-generated summaries at every branch show your reasoning</p>
                </div>
              </div>
            </div>

            {/* Who Uses Ramify */}
            <div className="mb-10 md:mb-12">
              <h3 className="text-xl md:text-2xl font-medium text-white/90 mb-4 text-center">
                Who uses Ramify:
              </h3>
              <div className="space-y-3">
                <p className="text-base md:text-lg text-white/70 leading-relaxed text-justify">
                  <span className="font-medium text-white">Researchers</span> who need to explore competing theories without losing their place.
                </p>
                <p className="text-base md:text-lg text-white/70 leading-relaxed text-justify">
                  <span className="font-medium text-white">Writers</span> developing characters and plot branches simultaneously.
                </p>
                <p className="text-base md:text-lg text-white/70 leading-relaxed text-justify">
                  <span className="font-medium text-white">Engineers</span> comparing solution architectures side-by-side.
                </p>
                <p className="text-base md:text-lg text-white/70 leading-relaxed text-justify">
                  <span className="font-medium text-white">Anyone</span> doing complex thinking who's tired of losing their best ideas to linear chat.
                </p>
              </div>
            </div>

            {/* In Short */}
            <div className="backdrop-blur-md bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-2xl p-6 md:p-8">
              <h3 className="text-xl md:text-2xl font-semibold text-white mb-3 text-center">
                In short:
              </h3>
              <p className="text-lg md:text-xl text-white/90 leading-relaxed text-justify">
                Ramify is the AI workspace for non-linear thinking. It's where scattered conversations become connected knowledge.
              </p>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mt-16 md:mt-24 mb-16 md:mb-20 relative z-50">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-semibold text-white text-center mb-12 relative z-50">
              Trusted by researchers pushing the boundaries
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* Testimonial 1 - Kyle Broder */}
              <div className="relative p-6 md:p-8 rounded-2xl" style={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)'
              }}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                    KB
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">Kyle Broder, PhD</h3>
                    <p className="text-white/60 text-sm">Mathematician</p>
                  </div>
                </div>
                <p className="text-white/80 leading-relaxed text-base">
                  "When working on complex proofs, I often explore multiple approaches simultaneously. Ramify lets me branch each direction without losing my previous work. It's transformed how I tackle difficult problems—I can finally see the entire solution space instead of following just one path."
                </p>
              </div>

              {/* Testimonial 2 - Lachlan Oberg */}
              <div className="relative p-6 md:p-8 rounded-2xl" style={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)'
              }}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                    LO
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">Lachlan Oberg, PhD</h3>
                    <p className="text-white/60 text-sm">Quantum Physicist, Quantum Computing</p>
                  </div>
                </div>
                <p className="text-white/80 leading-relaxed text-base">
                  "In quantum computing research, every conversation with AI generates ideas for different experimental setups or theoretical frameworks. Before Ramify, I'd lose track of promising directions. Now I can branch discussions, compare approaches side-by-side, and never lose context. It's essential for managing the complexity of cutting-edge research."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Table - Steve Jobs Style */}
        <div className="mt-20 md:mt-32 mb-16 md:mb-20 relative z-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-semibold text-white text-center mb-4">
              Why researchers choose Ramify
            </h2>
            <p className="text-white/60 text-center text-lg mb-12 max-w-2xl mx-auto">
              Stop choosing between AI and notes. Stop losing your best ideas to linear chat.
            </p>

            {/* Comparison Grid */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-white/60 font-normal text-sm md:text-base w-2/5"></th>
                    <th className="text-center py-4 px-4">
                      <div className="text-white font-semibold text-base md:text-lg mb-1">ChatGPT</div>
                      <div className="text-white/40 text-xs md:text-sm">AI Chat</div>
                    </th>
                    <th className="text-center py-4 px-4">
                      <div className="text-white font-semibold text-base md:text-lg mb-1">Notion</div>
                      <div className="text-white/40 text-xs md:text-sm">Notes</div>
                    </th>
                    <th className="text-center py-4 px-4 bg-white/5 rounded-t-xl">
                      <div className="text-emerald-400 font-semibold text-base md:text-lg mb-1">Ramify</div>
                      <div className="text-emerald-400/60 text-xs md:text-sm">AI + Notes + Visual</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Row 1 */}
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-4 text-white/80 text-sm md:text-base">Explore multiple solution paths</td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-red-400 text-xl">✗</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-red-400 text-xl">✗</span>
                    </td>
                    <td className="py-4 px-4 text-center bg-white/5">
                      <span className="text-emerald-400 text-xl">✓</span>
                    </td>
                  </tr>

                  {/* Row 2 */}
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-4 text-white/80 text-sm md:text-base">See your full thinking process visually</td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-red-400 text-xl">✗</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-yellow-400 text-xl">~</span>
                    </td>
                    <td className="py-4 px-4 text-center bg-white/5">
                      <span className="text-emerald-400 text-xl">✓</span>
                    </td>
                  </tr>

                  {/* Row 3 */}
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-4 text-white/80 text-sm md:text-base">Notes integrated with AI chats</td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-red-400 text-xl">✗</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-red-400 text-xl">✗</span>
                    </td>
                    <td className="py-4 px-4 text-center bg-white/5">
                      <span className="text-emerald-400 text-xl">✓</span>
                    </td>
                  </tr>

                  {/* Row 4 */}
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-4 text-white/80 text-sm md:text-base">Compare approaches side-by-side</td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-red-400 text-xl">✗</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-yellow-400 text-xl">~</span>
                    </td>
                    <td className="py-4 px-4 text-center bg-white/5">
                      <span className="text-emerald-400 text-xl">✓</span>
                    </td>
                  </tr>

                  {/* Row 5 */}
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-4 text-white/80 text-sm md:text-base">Never lose a promising direction</td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-red-400 text-xl">✗</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-yellow-400 text-xl">~</span>
                    </td>
                    <td className="py-4 px-4 text-center bg-white/5">
                      <span className="text-emerald-400 text-xl">✓</span>
                    </td>
                  </tr>

                  {/* Row 6 */}
                  <tr>
                    <td className="py-4 px-4 text-white/80 text-sm md:text-base">Real-time collaboration</td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-red-400 text-xl">✗</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-emerald-400 text-xl">✓</span>
                    </td>
                    <td className="py-4 px-4 text-center bg-white/5 rounded-b-xl">
                      <span className="text-emerald-400 text-xl">✓</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-12">
              <p className="text-white/60 text-sm md:text-base mb-6">
                The only workspace built for complex thinking with AI
              </p>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-lg text-base font-semibold transition-all duration-300 hover:scale-105 shadow-lg shadow-emerald-500/50"
              >
                Get Started Free
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>

    {/* Features Section with modern grid */}
    <div id="features-section" className="relative bg-black min-h-[auto] md:min-h-screen py-12 md:py-20 overflow-hidden z-10">

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-left mb-12 md:mb-16 mt-8 md:mt-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-white mb-6 md:mb-8">
            Follow your curiosity
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-white/60 max-w-3xl font-light">
            Every conversation can branch into new directions. Follow your curiosity down rabbit holes while keeping track of where you started. Never lose a thread of thought again.
          </p>
        </div>

        <Suspense fallback={<div className="h-[600px] flex items-center justify-center"><div className="animate-pulse text-white/40">Loading features...</div></div>}>
          <BentoGrid features={features} />
        </Suspense>

      </div>
    </div>

    {/* Use Cases Section */}
    <div className="relative bg-black min-h-screen py-12 md:py-20 overflow-hidden z-10 flex items-center">

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-left mb-12 md:mb-16 mt-8 md:mt-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-white mb-6 md:mb-8">
            Adapts to how you think, whatever your field
          </h2>
        </div>

        <Suspense fallback={<div className="h-[400px] flex items-center justify-center"><div className="animate-pulse text-white/40">Loading use cases...</div></div>}>
          <ExpandableCards cards={useCases} />
        </Suspense>
      </div>
    </div>

    {/* Early Voices - Testimonials Section */}
    {/* <div className="relative bg-black min-h-screen py-12 md:py-20 overflow-hidden z-10 flex items-center">

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-left mb-10 md:mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-white mb-4 md:mb-6">
            For minds that move the world
          </h2>
          <p className="text-base sm:text-lg text-white/60 max-w-2xl font-light">
            From the curious minds already exploring Ramify. NOTE: these are not real testimonials, they are placeholders for real testimonials. These will be deleted in the production version.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-6 md:p-8"
          >
            <div className="flex flex-col h-full">
              <p className="text-sm md:text-base text-white/80 font-light italic mb-6 flex-grow">
                "Finally, a way to explore ideas without losing my train of thought. I can branch conversations when I need to dive deeper, then come back to my original question. Game changer for research."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center">
                  <span className="text-white text-sm font-medium">SL</span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Sarah L.</p>
                  <p className="text-white/50 text-xs">PhD Candidate, MIT</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-6 md:p-8"
          >
            <div className="flex flex-col h-full">
              <p className="text-sm md:text-base text-white/80 font-light italic mb-6 flex-grow">
                "As a writer, I'm constantly going down rabbit holes. Ramify lets me follow every tangent without cluttering my main narrative. It's like having infinite notebooks that all stay organized."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500/30 to-orange-500/30 flex items-center justify-center">
                  <span className="text-white text-sm font-medium">MC</span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Marcus Chen</p>
                  <p className="text-white/50 text-xs">Sci-Fi Author</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-6 md:p-8"
          >
            <div className="flex flex-col h-full">
              <p className="text-sm md:text-base text-white/80 font-light italic mb-6 flex-grow">
                "Debugging complex issues used to mean juggling multiple chat threads. Now I can test different hypotheses in parallel branches and see which path leads to the solution. Brilliant."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500/30 to-teal-500/30 flex items-center justify-center">
                  <span className="text-white text-sm font-medium">AP</span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Aisha Patel</p>
                  <p className="text-white/50 text-xs">Senior Engineer, Stripe</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div> */}

    {/* AI Models Section */}
    <div className="relative bg-black min-h-screen py-12 md:py-20 overflow-hidden z-10 flex items-center">

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-left mb-10 md:mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-white mb-4 md:mb-6">
            Built with your favourite models
          </h2>
          <p className="text-base sm:text-lg text-white/60 max-w-2xl font-light">
            Access flagship models from all the major AI providers — all in one place
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {/* OpenAI Card */}
          <div
            onMouseEnter={() => setHoveredModelCard(0)}
            onMouseLeave={() => setHoveredModelCard(null)}
            className={cn(
              "backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-6 transition-all duration-300 ease-out",
              hoveredModelCard !== null && hoveredModelCard !== 0 && "blur-sm scale-[0.98]",
              hoveredModelCard === 0 && "bg-white/8"
            )}
          >
            <div className="flex flex-col items-center text-center">
              {/* OpenAI Logo */}
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <img
                  src="/OpenAI-white-monoblossom.svg"
                  alt="OpenAI"
                  className="w-20 h-20 object-contain"
                />
              </div>
              <h3 className="text-lg font-medium text-white mb-3">OpenAI</h3>
              <ul className="text-sm text-white/60 space-y-1.5 font-mono">
                <li>GPT-5</li>
                <li>O3</li>
                <li>GPT-4o</li>
                <li>and more</li>
              </ul>
            </div>
          </div>

          {/* Anthropic Card */}
          <div
            onMouseEnter={() => setHoveredModelCard(1)}
            onMouseLeave={() => setHoveredModelCard(null)}
            className={cn(
              "backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-6 transition-all duration-300 ease-out",
              hoveredModelCard !== null && hoveredModelCard !== 1 && "blur-sm scale-[0.98]",
              hoveredModelCard === 1 && "bg-white/8"
            )}
          >
            <div className="flex flex-col items-center text-center">
              {/* Anthropic Logo */}
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <img
                  src="/Anthropic_Symbol_0.svg"
                  alt="Anthropic"
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h3 className="text-lg font-medium text-white mb-3">Anthropic</h3>
              <ul className="text-sm text-white/60 space-y-1.5 font-mono">
                <li>Claude Opus 4</li>
                <li>Claude Sonnet 4</li>
                <li>Claude 3.5 Sonnet</li>
                <li>and more</li>
              </ul>
            </div>
          </div>

          {/* Google Gemini Card */}
          <div
            onMouseEnter={() => setHoveredModelCard(2)}
            onMouseLeave={() => setHoveredModelCard(null)}
            className={cn(
              "backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-6 transition-all duration-300 ease-out",
              hoveredModelCard !== null && hoveredModelCard !== 2 && "blur-sm scale-[0.98]",
              hoveredModelCard === 2 && "bg-white/8"
            )}
          >
            <div className="flex flex-col items-center text-center">
              {/* Gemini Logo */}
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <img
                  src="/Google_Gemini_logo.svg"
                  alt="Google Gemini"
                  className="w-full h-full object-contain scale-[2]"
                />
              </div>
              <h3 className="text-lg font-medium text-white mb-3">Google Gemini</h3>
              <ul className="text-sm text-white/60 space-y-1.5 font-mono">
                <li>Gemini 2.5 Pro</li>
                <li>Gemini 2.5 Flash</li>
              </ul>
            </div>
          </div>

          {/* xAI Grok Card */}
          <div
            onMouseEnter={() => setHoveredModelCard(3)}
            onMouseLeave={() => setHoveredModelCard(null)}
            className={cn(
              "backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-6 transition-all duration-300 ease-out",
              hoveredModelCard !== null && hoveredModelCard !== 3 && "blur-sm scale-[0.98]",
              hoveredModelCard === 3 && "bg-white/8"
            )}
          >
            <div className="flex flex-col items-center text-center">
              {/* Grok Logo */}
              <div className="w-16 h-16 mb-4 mt-2 flex items-center justify-center">
                <img
                  src="/grok-logo.svg"
                  alt="Grok"
                  className="w-full h-full object-contain scale-[2]"
                  style={{ filter: 'invert(1) brightness(0.9)' }}
                />
              </div>
              <h3 className="text-lg font-medium text-white mb-3">xAI Grok</h3>
              <ul className="text-sm text-white/60 space-y-1.5 font-mono">
                <li>Grok 4 Fast</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Quick Start Tutorial Section */}
    <div className="relative bg-black min-h-screen py-12 md:py-20 overflow-hidden z-10 flex items-center">
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="text-left mb-10 md:mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-white mb-4 md:mb-6">
            Quick Start Tutorial
          </h2>
          <p className="text-base sm:text-lg text-white/60 max-w-2xl font-light">
            Watch this short video to get started with Ramify
          </p>
        </div>

        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full rounded-xl"
            src="https://www.youtube.com/embed/7bDbREbH3Qg"
            title="Quick Start Tutorial"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{
              border: 'none',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            }}
          />
        </div>
      </div>
    </div>

    {/* Philosophy Section with gradient */}
    <div id="philosophy-section" className="relative bg-black min-h-[auto] md:min-h-screen py-12 md:py-20 flex items-center overflow-hidden z-10">

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-left">
          <div className="max-w-4xl backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-medium text-white mb-6 sm:mb-8 md:mb-12 leading-tight">
              A New Way to Think
            </h2>
            <p className="text-base sm:text-lg md:text-2xl lg:text-3xl text-white/70 font-light leading-relaxed">
            We're building the future of knowledge work. 
            </p>
          </div>
          <div className="flex flex-col items-center mt-6 md:mt-8">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 hover:scale-105 shadow-lg shadow-emerald-500/50"
            >
              Get Started Free
            </button>
            <p className="mt-3 text-white/50 text-xs sm:text-sm font-light">
              No credit card required • Sign up in 30 seconds
            </p>
          </div>
        </div>
      </div>

      {/* Social Media Icons - Bottom Right of Philosophy Section */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{
          delay: 0.5,
          duration: 0.8,
        }}
        className="absolute bottom-8 right-8 z-40 hidden md:flex flex-row gap-4"
      >
        <a
          href="https://twitter.com/getramify"
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 backdrop-blur-sm bg-white/10 border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300 hover:scale-110"
          aria-label="Follow us on X"
        >
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>
        <a
          href="https://facebook.com/ramify"
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 backdrop-blur-sm bg-white/10 border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300 hover:scale-110"
          aria-label="Follow us on Facebook"
        >
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </a>
        <a
          href="https://youtube.com/@ramify"
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 backdrop-blur-sm bg-white/10 border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300 hover:scale-110"
          aria-label="Subscribe on YouTube"
        >
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        </a>
        <a
          href="https://instagram.com/ramify"
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 backdrop-blur-sm bg-white/10 border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300 hover:scale-110"
          aria-label="Follow us on Instagram"
        >
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
          </svg>
        </a>
      </motion.div>
    </div>

    {/* Footer */}
    <footer className="relative bg-black border-t border-white/10 py-6 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8">
          <a
            href="/privacy"
            className="text-white/60 hover:text-white transition-all duration-300 text-sm font-light"
          >
            Privacy Policy
          </a>
          <span className="hidden sm:inline text-white/30">|</span>
          <a
            href="/terms"
            className="text-white/60 hover:text-white transition-all duration-300 text-sm font-light"
          >
            Terms of Use
          </a>
        </div>
      </div>
    </footer>
    </div>
  );
};

export default LandingPage;